import type { AccountAllocationGridReport } from '@components/AccountAllocationGrid/types';
import type { SubAccountCustomAllocationInput } from '@ovComponents/4-module/widgets/portfolio/components/buildCustomAllocationInput';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import type { WorkflowDataType } from '@ovComponents/4-module/workflowCompletion/workflowTypes';
import { useCallback, useContext } from 'react';
import {
  type StagedAccountAllocationBaseline,
  type StagedAccountAllocations,
  stagedAccountAllocationBaseline,
  stagedAccountAllocations,
} from '../accountAllocationStaging';

export type StagedAccountAllocationsState =
  | { status: 'ABSENT' }
  | { status: 'LOADING' }
  | {
      status: 'STAGED' | 'STALE_REPORT';
      staged: StagedAccountAllocations;
      baseline: StagedAccountAllocationBaseline | undefined;
    };

type StageAccountAllocationsInput = {
  matrix: SubAccountCustomAllocationInput[];
  lockedCells: Iterable<string>;
  uploadedCells: Iterable<string>;
};

type UseStagedAccountAllocationsInput = {
  householdClientGroupId: string | undefined;
  report: AccountAllocationGridReport | null;
};

export const readStagedAccountAllocations = (
  workflowData: WorkflowDataType,
  householdClientGroupId: string | undefined,
  currentPortfolioReportId: string | undefined,
): StagedAccountAllocationsState => {
  const staged = workflowData.stagedAccountAllocations;
  if (!staged) return { status: 'ABSENT' };
  if (staged.householdClientGroupId !== householdClientGroupId) {
    return { status: 'ABSENT' };
  }
  if (currentPortfolioReportId === undefined) return { status: 'LOADING' };

  return {
    status:
      staged.portfolioReportId === currentPortfolioReportId
        ? 'STAGED'
        : 'STALE_REPORT',
    staged,
    baseline: workflowData.stagedAccountAllocationBaseline,
  };
};

// householdClientGroupId is the id the step already resolved: workflows
// launched from a household page never set currentHouseholdClientGroupId.
export const useStagedAccountAllocations = ({
  householdClientGroupId,
  report,
}: UseStagedAccountAllocationsInput) => {
  const { workflowData, setWorkflowData } = useContext(WorkflowContext);

  const stage = useCallback(
    ({ matrix, lockedCells, uploadedCells }: StageAccountAllocationsInput) => {
      if (!householdClientGroupId || !report) return;

      const now = new Date();
      const staged = stagedAccountAllocations({
        householdClientGroupId,
        report,
        matrix,
        lockedCells,
        uploadedCells,
        stagedAt: now,
      });
      return setWorkflowData((previousData) => ({
        ...previousData,
        stagedAccountAllocations: staged,
        // Captured once per report: a re-stage must not erase what the accounts
        // held before the workflow, which the commit checks for drift against.
        stagedAccountAllocationBaseline:
          previousData.stagedAccountAllocationBaseline?.portfolioReportId ===
          report.id
            ? previousData.stagedAccountAllocationBaseline
            : stagedAccountAllocationBaseline(report, now),
      }));
    },
    [householdClientGroupId, report, setWorkflowData],
  );

  return {
    state: readStagedAccountAllocations(
      workflowData,
      householdClientGroupId,
      report?.id,
    ),
    stage,
  };
};
